import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { supabase } from '../services/supabase';

/**
 * Hook to detect and update user's timezone
 * Uses Intl.DateTimeFormat API to get browser timezone
 */
export const useTimezone = () => {
  const { user } = useAuth();
  const hasUpdatedTimezone = useRef(false);

  useEffect(() => {
    const updateUserTimezone = async () => {
      if (!user || hasUpdatedTimezone.current) return;

      try {
        // Get browser timezone
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        
        // Check if user already has this timezone set
        const currentTimezone = user.user_metadata?.timezone;
        if (currentTimezone === timezone) {
          hasUpdatedTimezone.current = true;
          return;
        }

        console.log('Updating user timezone to:', timezone);

        // Update user's timezone in Supabase metadata - only once
        const { error } = await supabase.auth.updateUser({
          data: {
            timezone: timezone
          }
        });

        if (error) {
          console.error('Failed to update timezone:', error);
        } else {
          console.log('User timezone updated successfully');
          hasUpdatedTimezone.current = true;
        }
      } catch (error) {
        console.error('Failed to update timezone:', error);
        // Don't throw - this is a non-critical enhancement
      }
    };

    updateUserTimezone();
  }, [user]);

  /**
   * Get the user's current timezone
   */
  const getUserTimezone = (): string => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  /**
   * Format a date in the user's timezone
   */
  const formatInUserTimezone = (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
    const timezone = getUserTimezone();
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      ...options
    }).format(dateObj);
  };

  /**
   * Get timezone offset in hours from UTC
   */
  const getTimezoneOffset = (): number => {
    const now = new Date();
    const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(now.toLocaleString('en-US', { timeZone: getUserTimezone() }));
    return (tzDate.getTime() - utcDate.getTime()) / (1000 * 60 * 60);
  };

  /**
   * Get a user-friendly timezone name (e.g., "Pacific Time" instead of "America/Los_Angeles")
   */
  const getTimezoneName = (): string => {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: getUserTimezone(),
      timeZoneName: 'long'
    });
    const parts = formatter.formatToParts(new Date());
    const timezonePart = parts.find(part => part.type === 'timeZoneName');
    return timezonePart?.value || getUserTimezone();
  };

  return {
    timezone: getUserTimezone(),
    formatInUserTimezone,
    getTimezoneOffset,
    getTimezoneName
  };
};